import { useState, useMemo, type ReactNode } from "react";
import { ArrowRightLeft, Inbox, Loader2 } from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Popover, PopoverAnchor, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import apiClient from "@/lib/api-client";
import { useRoutePaper } from "@/hooks/useRoutePaper";
import type { ConceptListItem } from "@/types/concept";

interface CrossConceptPaper {
  paper_id: string;
  title: string;
  similarity: number;
}

interface CrossConceptSuggestion {
  concept_id: string;
  concept_name: string;
  papers: CrossConceptPaper[];
}

interface Props {
  conceptId: string;
  paperId: string;
  isFoundational?: boolean;
  onRouted?: () => void;
  /** When provided, shows Inbox as a target. Called with mode so caller can flag + optionally dismiss. */
  onInbox?: (mode: "copy" | "move") => void;
  /** Controlled open — with `anchor`, the popover is opened from elsewhere (a menu item). */
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  /** Replaces the icon trigger with a positioning anchor. */
  anchor?: ReactNode;
}

export function RouteToConceptPopover({ conceptId, paperId, isFoundational, onRouted, onInbox, open: openProp, onOpenChange, anchor }: Props) {
  const [ownOpen, setOwnOpen] = useState(false);
  const open = openProp ?? ownOpen;
  const setOpen = onOpenChange ?? setOwnOpen;
  const [mode, setMode] = useState<"copy" | "move">("move");
  const routeMutation = useRoutePaper(conceptId);

  // Fetch cross-concept suggestions (only when popover is open)
  const { data: crossData } = useQuery({
    queryKey: ["cross-concept", conceptId, paperId],
    queryFn: () =>
      apiClient
        .get(`api/concepts/${conceptId}/papers/${paperId}/cross-concept`)
        .json<{ suggestions: CrossConceptSuggestion[] }>(),
    enabled: open,
    staleTime: 60_000,
  });

  // Fetch all concepts for the fallback list (only when popover is open)
  const { data: allConcepts } = useQuery({
    queryKey: ["concepts"],
    queryFn: () => apiClient.get("api/concepts").json<ConceptListItem[]>(),
    enabled: open,
    staleTime: 60_000,
  });

  // Derive suggested concept IDs with max similarity
  const suggestions = useMemo(() => {
    if (!crossData?.suggestions) return [];
    return crossData.suggestions.map((s) => ({
      concept_id: s.concept_id,
      concept_name: s.concept_name,
      similarity: Math.max(...s.papers.map((p) => p.similarity)),
    }));
  }, [crossData]);

  // All concepts minus current and suggested
  const otherConcepts = useMemo(() => {
    const suggestedIds = new Set(suggestions.map((s) => s.concept_id));
    return (allConcepts ?? []).filter((c) => c.id !== conceptId && !suggestedIds.has(c.id));
  }, [allConcepts, conceptId, suggestions]);

  // The row being copied/moved shows a spinner; the others wait (one route at a time).
  const routingTo = routeMutation.isPending ? routeMutation.variables?.targetConceptId : undefined;
  const handleRoute = (targetConceptId: string, targetConceptName: string) => {
    // Close immediately — toast fires optimistically in onMutate
    setOpen(false);
    if (mode === "move") onRouted?.();
    routeMutation.mutate({ paperId, targetConceptId, targetConceptName, mode });
  };

  const handleInbox = () => {
    setOpen(false);
    if (mode === "move") onRouted?.();
    onInbox?.(mode);
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      {anchor ? (
        <PopoverAnchor asChild>{anchor}</PopoverAnchor>
      ) : (
        <PopoverTrigger asChild>
          <Button
            variant="ghost"
            size="icon-sm"
            title="Route to another concept"
            className="text-violet-500/70 hover:text-violet-500"
          >
            <ArrowRightLeft className="size-3.5" />
          </Button>
        </PopoverTrigger>
      )}
      <PopoverContent className="w-64 p-3" align="start">
        <p className="mb-2 text-sm font-medium">Route Paper</p>

        {/* Copy / Move toggle */}
        <div className="mb-3 flex gap-1">
          <button
            className={`flex-1 rounded-md px-2 py-1 text-xs transition-colors ${
              mode === "move"
                ? "bg-blue-500/20 text-blue-400 border border-blue-500/50"
                : "bg-muted text-muted-foreground border border-transparent hover:bg-muted/80"
            }`}
            onClick={() => isFoundational ? undefined : setMode("move")}
            disabled={isFoundational}
            title={isFoundational ? "Seed papers can only be copied" : undefined}
          >
            Move
          </button>
          <button
            className={`flex-1 rounded-md px-2 py-1 text-xs transition-colors ${
              mode === "copy"
                ? "bg-blue-500/20 text-blue-400 border border-blue-500/50"
                : "bg-muted text-muted-foreground border border-transparent hover:bg-muted/80"
            }`}
            onClick={() => setMode("copy")}
          >
            Copy
          </button>
        </div>

        {/* Inbox target */}
        {onInbox && (
          <button
            className="mb-2 flex w-full items-center gap-2 rounded-md border border-amber-500/20 bg-amber-500/5 px-2.5 py-1.5 text-left text-xs text-foreground transition-colors hover:bg-amber-500/10"
            onClick={handleInbox}
          >
            <Inbox className="size-3.5 text-amber-500" />
            Inbox
          </button>
        )}

        {/* Suggested concepts */}
        {suggestions.length > 0 && (
          <>
            <p className="mb-1 text-[10px] uppercase tracking-wide text-muted-foreground/60">
              Suggested
            </p>
            <div className="mb-2 flex flex-col gap-1">
              {suggestions.map((s) => (
                <button
                  key={s.concept_id}
                  className="flex items-center justify-between rounded-md border border-violet-500/20 bg-violet-500/5 px-2.5 py-1.5 text-left text-xs text-foreground transition-colors hover:bg-violet-500/10"
                  onClick={() => handleRoute(s.concept_id, s.concept_name)}
                  disabled={routeMutation.isPending}
                >
                  <span className="inline-flex min-w-0 items-center gap-1.5">
                    {routingTo === s.concept_id && <Loader2 className="size-3 shrink-0 animate-spin" />}
                    <span className="truncate">{s.concept_name}</span>
                  </span>
                  <span className="ml-2 shrink-0 rounded-full bg-violet-500/15 px-1.5 py-0.5 text-[10px] text-violet-400">
                    {Math.round(s.similarity * 100)}%
                  </span>
                </button>
              ))}
            </div>
          </>
        )}

        {/* All concepts */}
        <p className="mb-1 text-[10px] uppercase tracking-wide text-muted-foreground/60">
          {suggestions.length > 0 ? "All Concepts" : "Concepts"}
        </p>
        <div className="flex max-h-48 flex-col gap-1 overflow-y-auto">
          {otherConcepts.map((c) => (
            <button
              key={c.id}
              className="rounded-md border border-border/50 bg-muted/30 px-2.5 py-1.5 text-left text-xs text-foreground transition-colors hover:bg-muted/60"
              onClick={() => handleRoute(c.id, c.name)}
              disabled={routeMutation.isPending}
            >
              <span className="inline-flex min-w-0 items-center gap-1.5">
                {routingTo === c.id && <Loader2 className="size-3 shrink-0 animate-spin" />}
                <span className="truncate">{c.name}</span>
              </span>
            </button>
          ))}
          {otherConcepts.length === 0 && suggestions.length === 0 && !onInbox && (
            <p className="py-2 text-center text-xs text-muted-foreground">No other concepts</p>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}
